import { useCallback, useEffect, useRef, useState } from 'react'
import { Download, HardDriveDownload, Loader2, Trash2, WifiOff } from 'lucide-react'

import {
  cacheAvailable,
  downloadBasemap,
  KEIN_ARCHIV,
  megabytes,
  offlineControls,
  progressPercent,
  readBasemapCache,
  removeBasemap,
  type BasemapCacheState,
  type DownloadProgress,
} from './offlineTiles'

interface Props {
  /** Liegt auf dem Server ein Kachelarchiv? Aus /api/v1/config. */
  hasBasemap: boolean
}

/**
 * Die Karte für die Reise herunterladen, ansehen, wieder loeschen.
 *
 * Ausdrücklich ein Knopf und kein Automatismus: das Archiv ist einige hundert
 * Megabyte gross, und niemand will das im Roaming oder auf einem knappen
 * Datenvertrag erklären müssen. Wer nicht drückt, hat eine Karte, die nur
 * online funktioniert - und genau das steht hier auch, statt es zu verschweigen.
 *
 * Was gezeigt wird, entscheidet offlineControls und nicht hasBasemap allein:
 * eine lokale Kopie muss sich auch dann loeschen lassen, wenn auf dem Server
 * kein Archiv mehr liegt.
 */
export function BasemapOffline({ hasBasemap }: Props) {
  const [state, setState] = useState<BasemapCacheState>(KEIN_ARCHIV)
  const [progress, setProgress] = useState<DownloadProgress | null>(null)
  const [error, setError] = useState<string | null>(null)
  const abbruch = useRef<AbortController | null>(null)

  const nachsehen = useCallback(() => {
    void readBasemapCache().then(setState)
  }, [])

  useEffect(() => {
    nachsehen()
    return () => {
      // Ein laufender Download wird abgebrochen, wenn die Ansicht verschwindet:
      // ohne Fortschrittsanzeige wüsste niemand mehr, dass er laeuft.
      abbruch.current?.abort()
    }
  }, [nachsehen])

  const laden = async () => {
    setError(null)
    const controller = new AbortController()
    abbruch.current = controller
    try {
      const ergebnis = await downloadBasemap(setProgress, controller.signal)
      setState(ergebnis)
    } catch (err) {
      if (controller.signal.aborted) return
      console.error('Kartenarchiv liess sich nicht laden', err)
      setError(err instanceof Error ? err.message : 'Der Download ist fehlgeschlagen.')
      nachsehen()
    } finally {
      abbruch.current = null
      setProgress(null)
    }
  }

  const loeschen = async () => {
    setError(null)
    try {
      await removeBasemap()
    } catch (err) {
      console.error('Kartenarchiv liess sich nicht löschen', err)
      setError('Das Archiv liess sich nicht löschen.')
    }
    nachsehen()
  }

  const bedienung = offlineControls({
    cache: cacheAvailable(),
    hasBasemap,
    stored: state.stored,
  })

  if (!bedienung.show) return null

  if (bedienung.unsupported) {
    return (
      <p className="karte-warnung">
        <WifiOff size={13} /> Dieser Browser kann die Karte nicht offline speichern. Sie
        funktioniert nur mit Netz.
      </p>
    )
  }

  const laeuft = progress !== null
  const prozent = progress ? progressPercent(progress.loaded, progress.total) : null

  return (
    <div className="karte-offline">
      <div className="karte-offline-text">
        {state.stored ? (
          <>
            <b>Karte liegt auf dem Gerät.</b>{' '}
            {state.bytes > 0 && <span>{megabytes(state.bytes)}</span>}
            {/*
              Fehlt das Archiv auf dem Server, bleibt die lokale Kopie brauchbar
              - nur neu laden lässt sie sich nicht mehr. Das gehört dazugesagt,
              sonst wirkt der fehlende Knopf wie ein Fehler.
            */}
            {!hasBasemap && (
              <span> Auf dem Server liegt derzeit keines, neu laden geht deshalb nicht.</span>
            )}
          </>
        ) : (
          <>
            <b>Karte ist nicht heruntergeladen.</b>{' '}
            <span>Ohne Archiv zeigt sie nur mit Netz etwas an.</span>
          </>
        )}
      </div>

      <div className="knopfreihe">
        {bedienung.download && !state.stored && (
          <button type="button" className="knopf" disabled={laeuft} onClick={() => void laden()}>
            {laeuft ? <Loader2 size={13} className="dreht" /> : <HardDriveDownload size={13} />}
            {laeuft ? 'Karte wird geladen …' : 'Karte für die Reise herunterladen'}
          </button>
        )}
        {bedienung.remove && (
          <button type="button" className="knopf gefahr" onClick={() => void loeschen()}>
            <Trash2 size={13} /> Karte vom Gerät löschen
          </button>
        )}
        {bedienung.download && state.stored && (
          <button
            type="button"
            className="knopf still"
            disabled={laeuft}
            onClick={() => void laden()}
          >
            <Download size={13} /> Neu laden
          </button>
        )}
      </div>

      {progress && (
        <div className="karte-fortschritt">
          <div
            className="karte-balken"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={prozent ?? undefined}
            aria-label="Fortschritt des Kartendownloads"
          >
            <span style={{ width: `${String(prozent ?? 0)}%` }} />
          </div>
          <span className="fortschritt">
            {megabytes(progress.loaded)}
            {progress.total > 0 && ` von ${megabytes(progress.total)}`}
            {prozent !== null && ` · ${String(prozent)} %`}
          </span>
        </div>
      )}

      {error && <p className="karte-warnung">{error}</p>}
    </div>
  )
}
